import { citationIdentity } from "../../../domains/gpt/citation";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { ExplainedChip, InfoPopover } from "./InfoPopover";
import { GRADE_STYLE, gradeLabel, JuisciGradeBadge } from "./JuisciGradeBadge";
import {
  CITATION_BADGE_CLASS,
  evidenceSourceOrder,
  verbatimQuotesBySource,
} from "./EvidenceAnswer";
import {
  type EvidenceGraphView,
  type EvidenceSourceView,
} from "../../../domains/gpt/hooks/useEvidenceGptChat";

type Claim = EvidenceGraphView["claims"][number];
type Edge = EvidenceGraphView["edges"][number];
type DroppedEdge = EvidenceGraphView["droppedEdges"][number];

// Claim status → chip palette, on the same green→orange certainty band the
// source signals use (SourceSignals.signalBand).
const STATUS_STYLE = {
  consensus: "border-emerald-200 bg-emerald-50 text-emerald-700",
  supported: "border-lime-200 bg-lime-50 text-lime-700",
  contested: "border-amber-200 bg-amber-50 text-amber-700",
  weak: "border-orange-200 bg-orange-50 text-orange-700",
} as const satisfies Record<Claim["status"], string>;

function statusLabel(
  status: Claim["status"],
  t: (key: string) => string
): string {
  switch (status) {
    case "consensus":
      return t("Consensus");
    case "supported":
      return t("Supported");
    case "contested":
      return t("Contested");
    case "weak":
      return t("Weak evidence");
    default:
      throw new Error(status satisfies never);
  }
}

// What each status means — the popover body, mirroring `deriveClaimStatus`
// (docs/juisci-gpt-evidence-graph.md §5) in clinician language.
function statusExplanation(
  status: Claim["status"],
  t: (key: string) => string
): string {
  switch (status) {
    case "consensus":
      return t(
        "A clinical practice guideline in the Juisci library supports this statement."
      );
    case "supported":
      return t(
        "A guideline, a systematic review or meta-analysis, or at least two independent sources support this statement."
      );
    case "contested":
      return t(
        "Cited sources both support and contradict this statement, so the disagreement is shown instead of a false consensus."
      );
    case "weak":
      return t(
        "Only a single lower-level source supports this statement, or the quotes only qualify it."
      );
    default:
      throw new Error(status satisfies never);
  }
}

const RELATION_STYLE = {
  supports: "bg-emerald-50 text-emerald-700",
  contradicts: "bg-red-50 text-red-700",
  qualifies: "bg-ds-slate-100 text-ds-slate-700",
} as const satisfies Record<Edge["relation"], string>;

function relationLabel(
  relation: Edge["relation"],
  t: (key: string) => string
): string {
  switch (relation) {
    case "supports":
      return t("Supports");
    case "contradicts":
      return t("Contradicts");
    case "qualifies":
      return t("Qualifies");
    default:
      throw new Error(relation satisfies never);
  }
}

function relationExplanation(
  relation: Edge["relation"],
  t: (key: string) => string
): string {
  switch (relation) {
    case "supports":
      return t("The quoted passage backs this statement in the cited source.");
    case "contradicts":
      return t(
        "The quoted passage disputes this statement in the cited source."
      );
    case "qualifies":
      return t(
        "The quoted passage adds a condition or limitation to this statement."
      );
    default:
      throw new Error(relation satisfies never);
  }
}

function droppedReasonLabel(
  reason: DroppedEdge["reason"],
  t: (key: string) => string
): string {
  switch (reason) {
    case "unknown_source":
      return t("Source could not be verified");
    case "unfaithful_passage":
      return t("Quote not found in the source");
    default:
      throw new Error(reason satisfies never);
  }
}

function droppedReasonExplanation(
  reason: DroppedEdge["reason"],
  t: (key: string) => string
): string {
  switch (reason) {
    case "unknown_source":
      return t(
        "The model cited a source that was not among the retrieved documents, so its quote could not be checked and was removed."
      );
    case "unfaithful_passage":
      return t(
        "The quoted text could not be found word-for-word in the cited source, so it was removed from the answer."
      );
    default:
      throw new Error(reason satisfies never);
  }
}

export const EVIDENCE_CHIP_CLASS =
  "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";

// One verified quote under its statement: its relation, the guideline grade it carries, and its cited source.
export function VerifiedQuote({
  relation,
  recommendationGrade,
  verbatim,
  source,
}: {
  relation: Edge["relation"];
  recommendationGrade: string | null;
  verbatim: string;
  source: { number: number; title: string; onOpen: () => void } | null;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1.5 border-l-2 border-ds-slate-100 pl-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <ExplainedChip
          className={cn(EVIDENCE_CHIP_CLASS, RELATION_STYLE[relation])}
          label={relationLabel(relation, t)}
          title={relationLabel(relation, t)}
        >
          {relationExplanation(relation, t)}
        </ExplainedChip>
        {recommendationGrade !== null && (
          <ExplainedChip
            className={cn(
              EVIDENCE_CHIP_CLASS,
              "bg-ds-slate-100 text-ds-slate-700"
            )}
            label={t("Guideline recommendation: {{grade}}", {
              grade: recommendationGrade,
            })}
            title={t("Guideline recommendation: {{grade}}", {
              grade: recommendationGrade,
            })}
          >
            {t(
              "The grade the cited guideline itself assigns to this recommendation, verified in the quoted passage."
            )}
          </ExplainedChip>
        )}
      </div>
      <blockquote className="m-0 text-sm/relaxed italic text-ds-slate-700">
        “{verbatim}”
      </blockquote>
      {source !== null && (
        <button
          type="button"
          onClick={source.onOpen}
          // `w-fit max-w-full`, not `self-start`: the button must hug its
          // content yet stay capped at the column, or the truncate below has
          // no bound and a long title pushes the whole panel past the viewport.
          className="flex w-fit max-w-full items-center gap-2 rounded-xl py-0.5 text-left hover:bg-ds-slate-50"
        >
          <span className={CITATION_BADGE_CLASS}>{source.number}</span>
          <span className="min-w-0 truncate text-xs text-ds-slate-600">
            {source.title}
          </span>
        </button>
      )}
    </div>
  );
}

/**
 * The Evidence tab's content for one turn: the full gated graph behind the
 * answer. Every claim with its status and confidence chips, each claim's
 * verified quotes (relation + verbatim + the cited source, opening the citation
 * sheet), and the edges the fidelity gate excluded — the audit trail that makes
 * the answer checkable, mirroring the ai-frontend `/evidence` view in the
 * webapp DS. Every chip explains itself (tap, or hover on desktop): what the
 * label means and how it is computed.
 */
export function EvidenceDetailsPanel({
  answer,
  onOpenSource,
}: {
  answer: EvidenceGraphView;
  onOpenSource: (
    source: EvidenceSourceView,
    citationNumber: number,
    quotes: readonly string[]
  ) => void;
}) {
  const { t } = useTranslation();

  const order = evidenceSourceOrder(answer);
  const numberById = new Map(order.map((id, index) => [id, index + 1]));
  const sourceById = new Map(
    answer.sources.map((source) => [source.id, source])
  );
  const quotesBySourceId = verbatimQuotesBySource(answer.edges);

  const edgesByClaimId = new Map<string, Edge[]>();
  for (const edge of answer.edges) {
    const existing = edgesByClaimId.get(edge.claimId);
    if (existing === undefined) edgesByClaimId.set(edge.claimId, [edge]);
    else existing.push(edge);
  }

  // The shared "how a status is computed" line, appended to every status
  // popover — the determinism guarantee is the same whatever the status is.
  const statusDerivation = t(
    "The status is computed automatically from the verified quotes and the sources' evidence levels, never chosen by the AI."
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {answer.juisciGrade !== null && (
          <JuisciGradeBadge grade={answer.juisciGrade} className="self-start" />
        )}
        {/* One slot, one message: the verified-quotes intro would contradict
            the empty state on an ungrounded turn (zero claims, where the tab
            still shows the gate's exclusions below). */}
        <p className="m-0 text-sm text-ds-slate-600">
          {answer.claims.length === 0
            ? t(
                "No claim could be grounded in a verifiable source for this question."
              )
            : t(
                "Each statement below is backed by quotes verified word-for-word in the cited sources."
              )}
        </p>
      </div>

      {answer.claims.map((claim) => {
        const claimEdges = edgesByClaimId.get(claim.id);
        return (
          <div
            key={claim.id}
            className="flex flex-col gap-2.5 rounded-2xl border border-ds-slate-100 bg-white p-4"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <ExplainedChip
                className={cn(
                  EVIDENCE_CHIP_CLASS,
                  "border",
                  STATUS_STYLE[claim.status]
                )}
                label={statusLabel(claim.status, t)}
                title={statusLabel(claim.status, t)}
              >
                <span className="block">
                  {statusExplanation(claim.status, t)}
                </span>
                <span className="mt-1 block">{statusDerivation}</span>
              </ExplainedChip>
              {claim.grade !== null && (
                <ExplainedChip
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full border px-2 py-0.5",
                    "text-xs font-semibold text-ds-black",
                    GRADE_STYLE[claim.grade].ring
                  )}
                  label={
                    <>
                      <span
                        className={cn(
                          "size-1.5 rounded-full",
                          GRADE_STYLE[claim.grade].dot
                        )}
                      />
                      {claim.grade}
                    </>
                  }
                  title={t("Juisci confidence score")}
                >
                  <span className="block">
                    {t(
                      "The strength of the evidence behind this statement. It uses each source's Level of Evidence, rises with corroborating sources, and falls with contested findings."
                    )}
                  </span>
                  <span className="mt-1 block">
                    {t("Confidence score")} {claim.grade}
                    {": "}
                    {gradeLabel(claim.grade, t)}
                  </span>
                </ExplainedChip>
              )}
            </div>
            <p className="m-0 text-sm/snug font-semibold text-ds-black">
              {claim.text}
            </p>
            {claimEdges !== undefined &&
              claimEdges.map((edge, index) => {
                const source = sourceById.get(edge.sourceId);
                const n = numberById.get(edge.sourceId);
                const identity =
                  source === undefined ? "" : citationIdentity(source);
                return (
                  <VerifiedQuote
                    key={index}
                    relation={edge.relation}
                    recommendationGrade={edge.recommendationGrade}
                    verbatim={edge.verbatim}
                    source={
                      source === undefined || n === undefined
                        ? null
                        : {
                            number: n,
                            title: `${source.title === null ? t("Untitled source") : source.title}${identity === "" ? "" : ` (${identity})`}`,
                            onOpen: () => {
                              const quotes = quotesBySourceId.get(source.id);
                              onOpenSource(
                                source,
                                n,
                                quotes === undefined ? [] : quotes
                              );
                            },
                          }
                    }
                  />
                );
              })}
          </div>
        );
      })}

      {answer.droppedEdges.length > 0 && (
        <div className="flex flex-col gap-2.5 rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
          <span className="flex items-center gap-1 text-xs font-semibold text-ds-slate-700">
            {t("Excluded by verification")}
            <InfoPopover label={t("About excluded statements")} align="left">
              {t(
                "Quotes the model proposed that could not be verified word-for-word in the cited source. They are not part of the answer."
              )}
            </InfoPopover>
          </span>
          {answer.droppedEdges.map((dropped, index) => (
            <div key={index} className="flex flex-col gap-1">
              <ExplainedChip
                className={cn(
                  EVIDENCE_CHIP_CLASS,
                  "self-start bg-amber-100 text-amber-800"
                )}
                label={droppedReasonLabel(dropped.reason, t)}
                title={droppedReasonLabel(dropped.reason, t)}
              >
                {droppedReasonExplanation(dropped.reason, t)}
              </ExplainedChip>
              <blockquote className="m-0 text-sm/relaxed italic text-ds-slate-600">
                “{dropped.edge.verbatim}”
              </blockquote>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
